/* iter-p3-y — Admin panel: per-author, per-tier royalty share.
 *
 * Every row is one author.  The "sub_tier" pill shows their current
 * subscription tier, and each of the 4 numeric inputs holds the AUTHOR's
 * royalty percentage for that tier (0-95).  Blank / missing = fall back
 * to the platform default (85%).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, RefreshCw, Percent } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function AuthorRoyaltyTiersPanel() {
  const [tiers,   setTiers]   = useState([]);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts,  setDrafts]  = useState({}); // user_id -> {tier: value}
  const [saving,  setSaving]  = useState(null);
  const [q,       setQ]       = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/authors/royalty-tiers`,
        { withCredentials: true, timeout: 15000 });
      setTiers(r.data?.tiers || []);
      setRows(r.data?.authors || []);
      setDrafts({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load authors.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const setDraft = (uid, tier, v) => {
    setDrafts((prev) => ({
      ...prev, [uid]: { ...(prev[uid] || {}), [tier]: v }
    }));
  };

  const save = async (row) => {
    const uid = row.user_id;
    const merged = { ...(row.royalty_pct_by_tier || {}) };
    Object.entries(drafts[uid] || {}).forEach(([tier, v]) => {
      const trimmed = String(v).trim();
      if (trimmed === '') { delete merged[tier]; return; }
      const n = Number(trimmed);
      if (Number.isFinite(n) && n >= 0 && n <= 95) merged[tier] = Math.round(n);
    });
    setSaving(uid);
    try {
      await axios.patch(`${API}/admin/authors/${uid}/royalty-tiers`,
        { royalty_pct_by_tier: merged },
        { withCredentials: true, timeout: 15000 });
      toast.success(`Updated royalty tiers for ${row.email || uid}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(null);
    }
  };

  const visible = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.email || '').toLowerCase().includes(s)
        || (r.name  || '').toLowerCase().includes(s)
        || (r.user_id || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4" data-testid="admin-royalty-tiers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Percent className="h-4 w-4 text-primary" /> Author Royalty Tiers
          </h2>
          <p className="text-[12px] text-muted-foreground">
            Per-author share of the gross for each subscription tier.
            Blank cell = fall back to the book&rsquo;s stored <code>royalty_pct</code> (default 85).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="royalty-tiers-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Reload
        </Button>
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search email, name, or user id…"
        data-testid="royalty-tiers-search"
      />

      <div className="overflow-auto rounded-md border border-border" data-testid="royalty-tiers-table-wrap">
        <table className="w-full text-left text-[12.5px]">
          <thead className="bg-muted/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 min-w-[220px]">Author</th>
              <th className="px-3 py-2 w-24">Sub Tier</th>
              {tiers.map((t) => (
                <th key={t} className="px-3 py-2 w-28">{t}</th>
              ))}
              <th className="px-3 py-2 w-20"> </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.user_id} className="border-t border-border"
                  data-testid={`royalty-tiers-row-${r.user_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.email || '(no email)'}</div>
                  <div className="text-[10.5px] text-muted-foreground">{r.name || r.user_id}</div>
                </td>
                <td className="px-3 py-2 text-[11px] uppercase tracking-wider">
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary font-semibold">
                    {r.sub_tier}
                  </span>
                </td>
                {tiers.map((t) => {
                  const cur = (r.royalty_pct_by_tier || {})[t];
                  const draft = drafts[r.user_id]?.[t];
                  const shown = draft !== undefined ? draft : (cur ?? '');
                  return (
                    <td key={t} className="px-3 py-2">
                      <Input
                        type="number"
                        min="0"
                        max="95"
                        placeholder="—"
                        value={shown}
                        onChange={(e) => setDraft(r.user_id, t, e.target.value)}
                        className="h-8 w-20 font-mono-stat text-[12px]"
                        data-testid={`royalty-tier-input-${r.user_id}-${t}`}
                      />
                    </td>
                  );
                })}
                <td className="px-3 py-2">
                  <Button
                    size="sm"
                    onClick={() => save(r)}
                    disabled={saving === r.user_id || !drafts[r.user_id]}
                    data-testid={`royalty-tier-save-${r.user_id}`}
                  >
                    {saving === r.user_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Save className="h-3.5 w-3.5" />}
                    Save
                  </Button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={tiers.length + 3} className="px-3 py-6 text-center text-muted-foreground">
                  No authors match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
